import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  parseISO,
  startOfDay,
  subMonths,
} from 'date-fns'
import memoize, { Key } from 'moize'

import arriereBackoffice, {
  BasePriceArgs,
  BookingStatus,
  BookingWindowItemSelection,
  DateRangeInput,
  DemandFunnelMarketAveragePeriod,
  DirectStaysArgs,
  EditNoteArgs,
  GetRatingsOverTimeArgs,
  Granularity,
  HomeHistorySnapshotKind,
  IDate,
  LeadSource,
  LinkRevenueKindTitlesArgs,
  MergeAndAcquisitionIncluded,
  OwnerIncomeDashboardArgs,
  PricingToolArgs,
  PricingToolCompsetV2Args,
  PricingToolEventTimelineArgs,
  PricingToolHealthCheckArgs,
  PricingToolHealthCheckItemSelection,
  PricingToolItemSelection,
  PricingToolItemV2Selection,
  PricingToolMinimumStayCalendarArgs,
  PricingToolMonthlyOccAndNetRevenueArgs,
  PricingToolNegativeReviewsArgs,
  PricingToolPropertyOptionsArgs,
  PricingToolPropertyOptionsItemSelection,
  PricingToolRevenueCalendarArgs,
  PricingToolRevenueCalendarItemSelection,
  PricingToolRevenueChannelBreakdownArgs,
  PricingToolRevenueDataArgs,
  PricingToolRevenueDataItemSelection,
  PricingToolWorklistReviewedIn,
  PricingToolWorklistStatus,
  PropertyBlocksArgs,
  RatingsOverTimeItemSelection,
  RecentBookingsArgs,
  RevenueActionsTimelineArgs,
  RevenueKindsArgs,
  RevenueKindTitlesArgs,
  RevenueModel,
  ReviewPeriodKind,
  ReviewRecentBookingArgs,
  SavePricingToolReviewNoChangesArgs,
  SeasonsArgs,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, formatDate, SHORT_ISO_DATE_FORMAT } from '@avantstay/backoffice-core'

import {
  MarketPerformanceAggregationArgs,
  ProfitAndLossDashboardArgs,
} from '@new/domains/properties/screens/propertyPandLDashboard/propertyPerformance.types'

import {
  BookingVolumeArgsProps,
  CustomerExperienceIndicatorsProps,
} from '@arriere/types/Dashboard.types'
import globalIndicators from '@queries/backoffice/globalIndicators.graphql'
import { trackError } from '@tracking/customEventReport'
import fetchApi from '@utils/fetchApi'

import { getHotelNamesById } from './hotels'

interface IndicatorsFilter {
  from: IDate
  to: IDate
  regionIds?: string[]
  revenueModel?: RevenueModel
}

type PricingToolRevenueCalendarMonthlyTargetSelection = {
  revenueTarget: true
  revenueTargetSource: true
}

type PricingToolRevenueCalendarYearlyAdditionalSelection = {
  yearlyRevenueTarget: true
  overallRentPAN: true
}

type PricingToolRevenueCalendarDailyBreakdownSelection = {
  fees: true
  services: true
  refundsAndCredits: true
  otherAdjustments: true
  appliedDiscounts: true
}

type PricingToolRevenueCalendarArgsWithRefunds = PricingToolRevenueCalendarArgs & {
  includeRefunds?: boolean
}

type PricingToolItemSelectionWithArr = PricingToolItemV2Selection & {
  asArr: true
  lyArr: true
  absMinPrice: true
}

type PricingToolWeeklyItemSelectionWithArr = PricingToolItemSelection & {
  asArrWeekend: true
  asArrWeekday: true
  lyArrWeekend: true
  lyArrWeekday: true
}

const pricingToolV2AdditionalSelection = {
  asArr: true,
  lyArr: true,
  absMinPrice: true,
} satisfies Pick<PricingToolItemSelectionWithArr, 'asArr' | 'lyArr' | 'absMinPrice'>

const pricingToolWeeklyArrSelection = {
  asArrWeekend: true,
  asArrWeekday: true,
  lyArrWeekend: true,
  lyArrWeekday: true,
} satisfies Pick<
  PricingToolWeeklyItemSelectionWithArr,
  'asArrWeekend' | 'asArrWeekday' | 'lyArrWeekend' | 'lyArrWeekday'
>

// Stopgap until @avantstay/arriere-clients is regenerated with the Vrbo price fields (DATA-3507).
type PricingToolDailyItemSelectionWithVrboPrice = PricingToolItemV2Selection & {
  vrboAdvertisedPrice: true
  vrboAvgStayPrice: true
}

type PricingToolWeeklyItemSelectionWithVrboPrice = PricingToolItemSelection & {
  vrboAdvertisedPriceWeekend: true
  vrboAdvertisedPriceWeekday: true
  vrboAvgStayPriceWeekend: true
  vrboAvgStayPriceWeekday: true
}

const pricingToolV2VrboPriceSelection = {
  vrboAdvertisedPrice: true,
  vrboAvgStayPrice: true,
} satisfies Pick<
  PricingToolDailyItemSelectionWithVrboPrice,
  'vrboAdvertisedPrice' | 'vrboAvgStayPrice'
>

const pricingToolWeeklyVrboPriceSelection = {
  vrboAdvertisedPriceWeekend: true,
  vrboAdvertisedPriceWeekday: true,
  vrboAvgStayPriceWeekend: true,
  vrboAvgStayPriceWeekday: true,
} satisfies Pick<
  PricingToolWeeklyItemSelectionWithVrboPrice,
  | 'vrboAdvertisedPriceWeekend'
  | 'vrboAdvertisedPriceWeekday'
  | 'vrboAvgStayPriceWeekend'
  | 'vrboAvgStayPriceWeekday'
>

type PricingToolRevenueDataItemSelectionWithOwnerAdr = PricingToolRevenueDataItemSelection & {
  lighthouseOwnerAdr: true
}

const pricingToolRevenueDataAdditionalSelection = {
  lighthouseOwnerAdr: true,
} satisfies Pick<PricingToolRevenueDataItemSelectionWithOwnerAdr, 'lighthouseOwnerAdr'>

type PricingToolRevenueCalendarItemSelectionWithAdditionalFields = Omit<
  PricingToolRevenueCalendarItemSelection,
  'dailyData' | 'monthlySummary' | 'yearlySummary'
> & {
  dailyData: NonNullable<PricingToolRevenueCalendarItemSelection['dailyData']> &
    PricingToolRevenueCalendarDailyBreakdownSelection
  monthlySummary: NonNullable<PricingToolRevenueCalendarItemSelection['monthlySummary']> &
    PricingToolRevenueCalendarMonthlyTargetSelection
  yearlySummary: NonNullable<PricingToolRevenueCalendarItemSelection['yearlySummary']> &
    PricingToolRevenueCalendarYearlyAdditionalSelection
}

const getNullableNumberField = (value: object | null | undefined, key: string) => {
  const fieldValue = value && key in value ? Reflect.get(value, key) : null
  return typeof fieldValue === 'number' ? fieldValue : null
}

const getNullableStringField = (value: object | null | undefined, key: string) => {
  const fieldValue = value && key in value ? Reflect.get(value, key) : null
  return typeof fieldValue === 'string' ? fieldValue : null
}

export const getGlobalReviewCountComparison = async ({
  fromDate,
  toDate,
  lastClosedFromDate,
  lastClosedToDate,
  source,
}: CustomerExperienceIndicatorsProps) => {
  const { data, errors } = await arriereBackoffice.queries.globaReviewCountComparison.raw({
    __alias: `globalReviewCountComparison${source.toLowerCase()}`,
    __args: {
      from: fromDate,
      to: toDate,
      comparison: { start: lastClosedFromDate, end: lastClosedToDate },
      source,
    },
    source: true,
    comparison: true,
    difference: true,
  })
  if (errors?.length) {
    trackError(`Attempt to fetch ${source} data failed: ${errors[errors?.length - 1]?.message}`, {
      fromDate,
      toDate,
    })
    throw new Error(errors[0]?.message)
  }
  return data
}

export const getGlobalReviewAvgRatingComparison = async ({
  fromDate,
  toDate,
  lastClosedFromDate,
  lastClosedToDate,
  source,
}: CustomerExperienceIndicatorsProps) => {
  const { data, errors } = await arriereBackoffice.queries.globaReviewAvgRatingComparison.raw({
    __alias: `globalReviewAvgRatingComparison${source.toLowerCase()}`,
    __args: {
      from: fromDate,
      to: toDate,
      comparison: { start: lastClosedFromDate, end: lastClosedToDate },
      source,
    },
    source: true,
    comparison: true,
    difference: true,
  })
  if (errors?.length) {
    trackError(`Attempt to fetch ${source} data failed: ${errors[errors?.length - 1]?.message}`, {
      fromDate,
      toDate,
    })
    throw new Error(errors[0]?.message)
  }
  return data
}

export const getGlobalReviewConversionComparison = async ({
  fromDate,
  toDate,
  lastClosedFromDate,
  lastClosedToDate,
  source,
}: CustomerExperienceIndicatorsProps) => {
  return arriereBackoffice.queries.globaReviewConversionComparison
    .raw({
      __alias: `globalReviewConversionComparison${source.toLowerCase()}`,
      __args: {
        from: fromDate,
        to: toDate,
        comparison: { start: lastClosedFromDate, end: lastClosedToDate },
        source,
      },
      source: true,
      comparison: true,
      difference: true,
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        trackError(
          `Attempt to fetch ${source} data failed: ${errors[errors?.length - 1]?.message}`,
          {
            fromDate,
            toDate,
          },
        )
        throw new Error(errors[0]?.message)
      }

      return data
    })
}

export const getRecentBookingVolume = ({
  alias,
  createdAtFrom,
  createdAtUntil,
  leadTimeFrom,
  leadTimeUntil,
  mergeAndAcqIncluded,
  regionIds,
  revenueModel,
}: BookingVolumeArgsProps) => {
  return arriereBackoffice.queries
    .globalIndicators({
      __alias: alias,
      __args: { regionIds, revenueModel },
      finance: {
        bookingVolumePerCheckInPeriod: {
          __args: {
            createdAtFrom,
            createdAtUntil,
            leadTimeFrom,
            leadTimeUntil,
            mergeAndAcqIncluded,
          },
          amount: true,
          morAmount: true,
        },
      },
    })
    .then(response => {
      return (response.finance.bookingVolumePerCheckInPeriod ?? []).map(result => ({
        amount: result.amount,
        morAmount: result.morAmount,
        nonMorAmount: result.amount - result.morAmount,
      }))
    })
}

export const getSuperhostDashboard = () => {
  return arriereBackoffice.queries.superHostDashboard({
    __alias: 'getSuperhostDashboard',
    startNextSuperHostPeriod: true,
    totalReviewConversionRate: true,
    totalAccountsWithSuperhost: true,
    totalListingCountOfSuperhostAccounts: true,
    totalAccountsTrendingSuperhost: true,
    totalListingCountOfTrendingSuperhostAccounts: true,
    totalAccountsAtRiskOfLosingSuperhost: true,
    totalListingCountOfAtRiskOfLosingSuperhostAccounts: true,
  })
}

export const getGuestFavorite = (regionIds?: string[]) => {
  return arriereBackoffice.queries
    .airbnbDistribution({
      __alias: 'getGuestFavorite',
      getGlobalListingsQualityScores: {
        __args: {
          regionIds,
        },
        guestFavoriteCount: true,
        guestFavoritePercent: true,
        lastUpdatedAt: true,
        listingsCount: true,
        tags: {
          count: true,
          percent: true,
          tag: true,
        },
      },
    })
    .then(response => response.getGlobalListingsQualityScores)
}

export const getGlobalIndicators = (params: {
  fromDate: IDate
  toDate: IDate
  fromComparativePeriod: IDate
  toComparativePeriod: IDate
  lastClosedFromDate: IDate
  lastClosedToDate: IDate
  regionIds?: string[]
  onlyStabilized: true
  revenueModel?: RevenueModel
  granularity: Granularity
  comparison?: DateRangeInput
  dateComparison?: DateRangeInput
}) => {
  return fetchApi(globalIndicators, params)
}

export const getHomesCounting = ({ regionIds }: { regionIds?: string[] }) => {
  return arriereBackoffice.queries.homesCountingStats({
    __alias: 'homesCountingStats',
    __args: {
      regionIds,
    },
    signedUnitsCount: true,
    liveUnitsCount: true,
    onBoardingCount: true,
    onBoardedCount: true,
    preListedCount: true,
    listedCount: true,
    pipelineCount: true,
    rampingCount: true,
    newCount: true,
    stabilizedCount: true,
  })
}

// Hardcoded booking-volume plan. (The revenue plan now comes from the companyPlan API.)
export const PLANNED_BOOKING_VOLUME = [
  { date: '2026-01-01', booked: 27_972_015 },
  { date: '2026-02-01', booked: 23_406_526 },
  { date: '2026-03-01', booked: 26_378_028 },
  { date: '2026-04-01', booked: 24_339_521 },
  { date: '2026-05-01', booked: 25_443_188 },
  { date: '2026-06-01', booked: 25_357_512 },
  { date: '2026-07-01', booked: 24_774_198 },
  { date: '2026-08-01', booked: 26_069_239 },
  { date: '2026-09-01', booked: 23_330_200 },
  { date: '2026-10-01', booked: 23_542_074 },
  { date: '2026-11-01', booked: 24_486_551 },
  { date: '2026-12-01', booked: 23_047_372 },
]

export interface CompanyPlanArgs {
  monthStart: string
  monthEnd: string
}

export const getCompanyPlan = async ({ monthStart, monthEnd }: CompanyPlanArgs) => {
  return arriereBackoffice.queries.companyPlan({
    __alias: 'getCompanyPlan',
    __args: { monthStart, monthEnd },
    latest: {
      month: true,
      planName: true,
      planVersion: true,
      planAmount: true,
    },
    versions: {
      planName: true,
      planVersion: true,
      month: true,
      planAmount: true,
    },
  })
}

export const getFutureRevenue = async ({ from, to, regionIds, revenueModel }: IndicatorsFilter) => {
  return arriereBackoffice.queries
    .globalIndicators({
      __alias: 'getFutureRevenue',
      __args: {
        regionIds,
        granularity: Granularity.monthly,
        revenueModel,
      },
      finance: {
        futureRevenue: {
          __args: {
            from,
            to,
            mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
          },
          date: true,
          booked: true,
        },
      },
    })
    .then(response => response.finance)
}

export const getMonthlyBookingVolume = async ({
  from,
  to,
  regionIds,
  revenueModel,
}: IndicatorsFilter) => {
  return arriereBackoffice.queries
    .globalIndicators({
      __alias: 'getMonthlyBookingVolume',
      __args: {
        regionIds,
        granularity: Granularity.monthly,
        revenueModel,
      },
      finance: {
        bookingVolume: {
          __args: {
            from,
            to,
            mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
          },
          values: {
            value: true,
            date: true,
          },
        },
      },
    })
    .then(response => response.finance.bookingVolume?.values)
}

export const getProfitAndLossDashboard = ({
  otaDemandMetricsPeriod,
  calendarAvailabilityPeriod,
  getAllListings,
  pricingToolCalendar,
  market,
}: ProfitAndLossDashboardArgs) => {
  return (
    arriereBackoffice.queries
      .assetPerformance({
        __alias: 'profitAndLossDashboard',
        __args: {
          getAllListings,
          market,
        },
        otaChannelUpdates: {
          direct: true,
          vrbo: true,
          airbnb: true,
        },
        results: {
          assignedFO: {
            name: true,
            phone: true,
            email: true,
          },
          otaChannels: {
            __args: {
              period: otaDemandMetricsPeriod,
            },
            airbnbSearchImpressions: true,
            airbnbBookingConversionRate: true,
            airbnbBookings: true,
            airbnbPDP: true,
            airbnbPDPConversionRate: true,
            airbnbPDPWeight: true,
            vrboBookingConversionRate: true,
            vrboPDPConversionRate: true,
            vrboReviews: true,
            vrboReviewsScore: true,
            vrboSearchImpressions: true,
            otaTotalBookingConversion: true,
            otaTotalPDPConversion: true,
            otaTotalSearchImpressions: true,
            directSearchImpressions: true,
            directBookingConversionRate: true,
            directPDPConversionRate: true,
            directPDP: true,
          },
          reservedAvailability: {
            __args: {
              period: calendarAvailabilityPeriod,
            },
            availableNights: true,
            blockedNights: true,
            reservedNights: true,
          },
          pricingTool: {
            __args: {
              period: pricingToolCalendar,
            },
            totalExpectedWeekdayPrice: true,
            totalAdnaPriceWeekday: true,
            weekdayPricePercentage: true,
            priceGradeWeekday: true,
            totalExpectedWeekendPrice: true,
            totalAdnaPriceWeekend: true,
            weekendPricePercentage: true,
            priceGradeWeekend: true,
            averageAsOccupancyWeekday: true,
            averageAsOccupancyWeekend: true,
            averageExternalOccupancyWeekday: true,
            averageExternalOccupancyWeekend: true,
            weekdayActionSuggestion: true,
            weekendActionSuggestion: true,
            weekdayActionSuggestionPercentage: true,
            weekendActionSuggestionPercentage: true,
            weekdayActionSuggestionAvg: true,
            weekendActionSuggestionAvg: true,
            weekdayActionSuggestionPercentageAvg: true,
            weekendActionSuggestionPercentageAvg: true,
          },
          accountStatus: true,
          actualsLTM: true,
          acquisitionChannel: true,
          address: true,
          airbnbReviews: true,
          airbnbReviewsScore: true,
          airDnaLTM: true,
          assetType: true,
          bathrooms: true,
          bedrooms: true,
          bookingsByChannelL90D: {
            amount: true,
            source: true,
          },
          bookingVolumeL90D: true,
          completeAirdnaRentalizer: true,
          daysListed: true,
          financialHealth: true,
          guestReadinessStatus: true,
          halfBathrooms: true,
          homeOwnerNpsReviews: {
            period: true,
            score: true,
          },
          leaseCost: true,
          listedDate: true,
          listingStatus: true,
          liveDate: true,
          liveDays: true,
          ltmContributionMargin: true,
          ltmGuestNps: true,
          managementFee: true,
          market: true,
          marketId: true,
          maxOccupancy: true,
          numberOfReviews: true,
          ntmBookedRevenue: true,
          partner: true,
          performance: true,
          portfolio: true,
          propertyId: true,
          propertyName: true,
          rating: true,
          responseTime: true,
          revenueModel: true,
          signedDate: true,
          superHost: true,
          terminationDate: true,
          isLTROnly: true,
          updatedAt: true,
          zipcode: true,
          y2Underwriting: true,
          january: true,
          february: true,
          march: true,
          april: true,
          may: true,
          june: true,
          july: true,
          august: true,
          september: true,
          october: true,
          november: true,
          december: true,
          availableNights: true,
          availableNightsNext90d: true,
          blockedNights: true,
          reservedNights: true,
          termEndDate: true,
          termStartDate: true,
          ownerEscalations: true,
          freshEscalations: true,
          nightsFloorRateReachedNext90d: true,
          isAffectedByReviews: true,
          dslb: true,
          dslbLatestSnapshotDate: true,
          latestBooking: true,
          allNonRealizedBookedRevenue: true,
          allNonRealizedOwnerRevenue: true,
          propertyType: true,
          state: true,
          yoyActualsLTMGrowthPercentage: true,
          priorYearActualsLTM: true,
          performanceSummary: {
            overallRating: true,
            immediateActionRequired: true,
          },
          qualityStandardsState: true,
        },
      })
      // flattening
      .then(response => ({
        ...response,
        results: response.results.map(row => ({
          ...row,
          ...row.pricingTool,
          ...row.reservedAvailability,
          ...row.otaChannels,
        })),
      }))
  )
}

export const getOwnerIncomeDashboard = ({
  args,
  aliasPrefix = '',
}: {
  args: OwnerIncomeDashboardArgs
  aliasPrefix?: string
}) => {
  return arriereBackoffice.queries
    .ownerIncomeDashboard({
      __alias: `${aliasPrefix ? `${aliasPrefix}_` : ''}ownerIncomeDashboard`,
      __args: args,
      results: {
        accountingPeriod: true,
        accountingPeriodDays: true,
        bookingGuestName: true,
        bookingStatus: true,
        bookingCancellationDate: true,
        bookingOtaId: true,
        bookingVoyageHash: true,
        bookingVoyageUrl: true,
        bookingVoyageId: true,
        bookingReservationType: true,
        bookingSource: true,
        bookingCreatedAt: true,
        bookingCheckIn: true,
        bookingCheckOut: true,
        bookingConfirmationId: true,
        homeId: true,
        homeName: true,
        homeRevenueModel: true,
        homeUnitId: true,
        homeUnitName: true,
        homeUnitRevenueModel: true,
        revenueRentalIncome: true,
        revenueAccidentalDamageProtection: true,
        revenueCCFee: true,
        revenueCancellationFee: true,
        revenueCancellationFeeAbsorbed: true,
        revenueChannelFeeDiscrepancy: true,
        revenueChannelManagementFee: true,
        revenueChefServices: true,
        revenueCleaningFee: true,
        revenueConciergeItems: true,
        revenueConsumablesProgram: true,
        revenueCouponDiscount: true,
        revenueDamageCollection: true,
        revenueDiscount: true,
        revenueDiscountAvantstayAbsorbed: true,
        revenueDiscountOwnerAsShared: true,
        revenueEarlyCheckIn: true,
        revenueElectronicLockFee: true,
        revenueEntertainment: true,
        revenueEventFee: true,
        revenueExperiences: true,
        revenueFine: true,
        revenueFirewood: true,
        revenueGet: true,
        revenueGolfCart: true,
        revenueGroceryServices: true,
        revenueGuestService: true,
        revenueHoaResortFee: true,
        revenueLateCheckOut: true,
        revenueLinenService: true,
        revenueMaintenanceFee: true,
        revenueMembershipDiscount: true,
        revenueMidStayCleaningFee: true,
        revenueOtaFee: true,
        revenueParkingFee: true,
        revenuePetDeposit: true,
        revenuePetFee: true,
        revenuePoolSpaHeatingFee: true,
        revenueReservationFee: true,
        revenueSecurityDeposit: true,
        revenueService: true,
        revenueTot: true,
        revenueTaxesOwnerRemit: true,
        revenueTbid: true,
        revenueTat: true,
        revenueTransportation: true,
        revenueTravelInsurance: true,
        revenueUtilityIncome: true,
        revenueVasOther: true,
        revenueVaykBeachRentals: true,
        revenueWelcomeAmenityFee: true,
        revenueWellness: true,
        revenueOther: true,
        revenueNotOnReport: true,
        revenueTotal: true,
        revenueGolfCartCleaningFee: true,
        pages: true,
        rowsCount: true,
        revenueDisputeAdjustment: true,
        revenueMarginalCleaningFee: true,
      },
    })
    .then(({ results }) => results)
}

export const getMarketAggregatedPerformanceValues = ({
  market,
  otaDemandMetricsPeriod,
  calendarAvailabilityPeriod,
  accountStatus,
  airDnaLTM,
  performance,
  actualsLTM,
  guestReadinessStatus,
  getAllListings,
}: MarketPerformanceAggregationArgs) => {
  return arriereBackoffice.queries.marketPerformanceAggregation
    .raw({
      __alias: 'marketPerformanceAggregation',
      __args: {
        market,
        otaDemandMetricsPeriod,
        calendarAvailabilityPeriod,
        accountStatus,
        airDnaLTM,
        performance,
        actualsLTM,
        guestReadinessStatus,
        getAllListings,
      },
      results: {
        market: true,
        marketId: true,
        sumLTMRevenue: true,
        sumAirDnaUnderwriting: true,
        sumPerformance: true,
        sumConversions: true,
        sumNumberOfReviews: true,
        sumRating: true,
        count: true,
        avgLTMRevenue: true,
        avgAirDnaUnderwriting: true,
        avgPerformance: true,
        avgAirbnbAppearanceInSearch: true,
        avgAirbnbPDP: true,
        avgConversions: true,
        avgNumberOfReviews: true,
        avgRating: true,
        avgAirbnbPDPConversionRate: true,
        avgTotalOtaBookingConversion: true,
        avgTotalOtaSearchImpressions: true,
        avgTotalOtaPDPConversion: true,
        avgVrboBookingConversion: true,
        avgVrboSearchImpressions: true,
        avgVrboPDPConversion: true,
        avgLtmContributionMargin: true,
        avgDirectChannelsAppearanceInSearch: true,
        avgDirectChannelsBookingConversionRate: true,
        avgDirectChannelsPDPConversionRate: true,
        avgDirectChannelsPDP: true,
        avgY2Underwriting: true,
        sumCompleteAirdnaRentalizer: true,
        avgAvailableNights: true,
        avgReservedNights: true,
        avgBlockedNights: true,
        avgAvailableNightsNext90d: true,
        avgNightsFloorRateReachedNext90d: true,
        avgOwnerEscalations: true,
        avgFreshEscalations: true,
        avgNtmBookedRevenue: true,
        avgAllNonRealizedBookedRevenue: true,
        avgAllNonRealizedOwnerRevenue: true,
        avgManagementFee: true,
        avgLtmGuestNps: true,
        avgAirbnbReviews: true,
        avgAirbnbReviewsScore: true,
        avgVrboReviews: true,
        avgVrboReviewsScore: true,
        avgHomeOwnerNpsScore: true,
      },
    })
    .then(response => response.data.results)
}

export const getAirbnbTripIssuesData = async () => {
  return arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getAirbnbTripIssuesData',
      aggregatedQualityStandards: {
        state: true,
        count: true,
        lastUpdatedAt: true,
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data.aggregatedQualityStandards
    })
}

interface GetAirbnbTripIssuesDataPeriodOverPeriodProps {
  fromDate?: IDate
  toDate?: IDate
  regionIds?: string[]
}

export const getAirbnbTripIssuesDataPeriodOverPeriod = async ({
  fromDate,
  toDate,
  regionIds,
}: GetAirbnbTripIssuesDataPeriodOverPeriodProps) => {
  return arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getAirbnbTripIssuesDataPeriodOverPeriod',
      qualityStandardDailySummary: {
        __args: {
          sourceDate: toDate ?? new Date(),
          comparisonDate: fromDate,
          regionIds,
        },
        source: {
          additionalWarn: true,
          educate: true,
          good: true,
          pendingRemoval: true,
          probation: true,
          removed: true,
          suspend: true,
          warn: true,
        },
        comparison: {
          additionalWarn: true,
          educate: true,
          good: true,
          pendingRemoval: true,
          probation: true,
          removed: true,
          suspend: true,
          warn: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data.qualityStandardDailySummary
    })
}

export const getPricingToolRevenueFeeStatsMarket = async ({
  marketId,
  startDate,
  endDate,
}: {
  marketId: string
  startDate?: string
  endDate?: string
}) => {
  return arriereBackoffice.queries
    .pricingToolRevenueFeeStatsMarket({
      __alias: 'getPricingToolRevenueFeeStatsMarket',
      __args: { marketId, startDate, endDate },
      results: {
        propertyId: true,
        propertyName: true,
        feesPercentage: true,
        totalRevenue: true,
      },
    })
    .then(async data => data.results)
}

export const getPricingToolRevenueFeeStats = async ({
  propertyId,
  startDate,
  endDate,
}: {
  propertyId: string
  startDate?: string
  endDate?: string
}) => {
  return arriereBackoffice.queries
    .pricingToolRevenueFeeStats({
      __alias: 'getPricingToolRevenueFeeStats',
      __args: { propertyId, startDate, endDate },
      result: {
        stats: {
          fees: true,
          feesPercentage: true,
          rental: true,
          rentalPercentage: true,
        },
      },
    })
    .then(async data => data.result?.stats)
}

export const getPricingToolRevenueFees = async ({
  propertyId,
  startDate,
  endDate,
}: {
  propertyId: string
  startDate?: string
  endDate?: string
}) => {
  const revenueBreakdownPromise = arriereBackoffice.queries
    .pricingToolLtmFeeRevenueProportion({
      __alias: 'getPricingToolRevenueFees',
      __args: { propertyId, startDate, endDate },
      results: {
        absoluteAmount: true,
        internalTaxFeeId: true,
        itemName: true,
        parentPropertyKey: true,
        percentageOfTotal: true,
        propertyId: true,
        totalRevenue: true,
      },
    })
    .then(
      fees =>
        fees.results?.sort((a, b) =>
          (a.absoluteAmount ?? 0) > (b.absoluteAmount ?? 0) ? -1 : 1,
        ) ?? [],
    )
  const propertyFeesPromise = arriereBackoffice.queries
    .home({
      __alias: 'pricingToolPropertyFees',
      __args: { homeId: propertyId },
      taxes: {
        tax: {
          isTax: true,
          enabled: true,
          name: true,
          value: true,
          valueType: true,
          unit: true,
          revenueTargets: true,
          id: true,
          archived: true,
          lastUpdateMeta: {
            changedAt: true,
            changedBy: true,
          },
        },
        enabled: true,
        value: true,
      },
    })
    .then(async data =>
      data.taxes
        .filter(i => i.enabled && !i.tax.isTax && !i.tax.archived)
        .map(({ tax: { id, name, value, valueType, unit }, value: homeValue }) => ({
          id,
          name,
          value: homeValue ?? value,
          valueType,
          unit,
        })),
    )
  return Promise.all([revenueBreakdownPromise, propertyFeesPromise]).then(
    ([revenueBreakdown, propertyFees]) => {
      return revenueBreakdown.map(item => {
        const fee = propertyFees.find(i => i.id === item.internalTaxFeeId)
        return { ...item, ...fee }
      })
    },
  )
}

export const getRevenueKindTitles = ({
  year,
  month,
  titleFilter,
  kindId,
}: RevenueKindTitlesArgs) => {
  return arriereBackoffice.queries
    .revenueKindTitles({
      __alias: 'revenueKindTitles',
      __args: {
        year,
        month,
        titleFilter,
        kindId,
      },
      revenueKindId: true,
      title: true,
      mapped: true,
      affectedBookings: true,
      affectedBookingExamples: true,
      totalAmount: true,
    })
    .then(response => response)
}

export const getRevenueKinds = ({ year, month, nameFilter }: RevenueKindsArgs) => {
  return arriereBackoffice.queries
    .revenueKinds({
      __alias: 'revenueKinds',
      __args: {
        year,
        month,
        nameFilter,
      },
      id: true,
      name: true,
      nTitles: true,
      glAccount: true,
      lastModified: true,
      titles: {
        revenueKindId: true,
        title: true,
        mapped: true,
        affectedBookings: true,
        affectedBookingExamples: true,
        totalAmount: true,
      },
    })
    .then(response => response)
}

export const linkRevenueKindTitles = ({ titleByKind }: LinkRevenueKindTitlesArgs) => {
  return arriereBackoffice.mutations
    .linkRevenueKindTitles({
      __alias: 'linkRevenueKindTitles',
      __args: {
        titleByKind,
      },
    })
    .then(response => {
      Alert.Success('Revenue title linked successfully')
      return response
    })
}

export const getPricingToolHealthCheck = async ({
  propertyId,
  marketId,
}: PricingToolHealthCheckArgs) => {
  return arriereBackoffice.queries
    .pricingToolHealthCheck({
      __alias: 'pricingToolHealthCheck',
      __args: {
        propertyId,
        marketId,
      },
      result: {
        statuses: {
          grade: true,
          number: true,
        },
        elements: {
          category: true,
          grade: true,
          rules: {
            grade: true,
            formula: true,
            description: true,
          },
          subCategory: true,
          value: true,
          comparisonValue: true,
          comparisonAgainst: true,
          comparisonVariation: true,
          type: true,
          comparisons: {
            grade: true,
            label: true,
            against: true,
            value: true,
            variation: true,
          },
          subElements: {
            category: true,
            comparisons: {
              grade: true,
              label: true,
              against: true,
              value: true,
              variation: true,
            },
            grade: true,
            rules: {
              grade: true,
              formula: true,
              description: true,
            },
            type: true,
            value: true,
          },
          valueString: true,
          comparisonValueString: true,
          dataQuality: {
            availability: true,
            confidence: true,
            dataPoints: true,
            minimumRequired: true,
            reason: true,
          },
        },
        gradeCounts: {
          EXCELLENT: true,
          GOOD: true,
          FAIR: true,
          POOR: true,
          NO_DATA: true,
          INSUFFICIENT_DATA: true,
          TOTAL: true,
        },
      } satisfies PricingToolHealthCheckItemSelection,
    })
    .then(response => response.result)
}

export const getPricingToolData = async ({ marketId, propertyId }: PricingToolArgs) => {
  return arriereBackoffice.queries
    .pricingTool({
      __alias: 'pricingToolWeeklyData',
      __args: {
        marketId,
        propertyId,
      },
      results: {
        medianCompSetCleaningFee: true,
        address: true,
        weekdayDate: true,
        weekendDate: true,
        propertyName: true,
        avgYstdWeekend: true,
        avgYstdWeekday: true,
        adnaPriceWeekend: true,
        adnaPriceWeekday: true,
        ...pricingToolWeeklyVrboPriceSelection,
        asDesirabilityScore: true,
        adnaWeekendSpread: true,
        adnaWeekdaySpread: true,
        asAdrWeekend: true,
        asAdrWeekday: true,
        ...pricingToolWeeklyArrSelection,
        extAdrWeekend: true,
        extAdrWeekday: true,
        extOccWeekend: true,
        asOccWeekend: true,
        extOccWeekday: true,
        asOccWeekday: true,
        avgWeekendNetRevenue: true,
        avgWeekdayNetRevenue: true,
        season: true,
        market: true,
        bookingCodes: true,
        leadTimes: true,
        asMinStay: true,
        asHasPool: true,
        asHasAc: true,
        asPropertyIsWaterFront: true,
        asHasCommunalPool: true,
        asHasHotTub: true,
        asMaxOccupancy: true,
        asBedrooms: true,
        asBathrooms: true,
        asAirbnbUrl: true,
        asVrboUrl: true,
        adnaCalendarDate: true,
        datasetLastUpdatedAt: true,
        compSetSize: true,
        marketId: true,
        propertyId: true,
        avgFloorRateWeekday: true,
        avgFloorRateWeekend: true,
        asAvailableNightsNext90Days: true,
        asAbnbScore: true,
        asAbnbReviewCount: true,
        compSetAprWeekday: true,
        compSetAprWeekend: true,
        compSetAprWeekdayStddev: true,
        compSetAprWeekendStddev: true,
        asLatitude: true,
        asLongitude: true,
        ltmRevenue: true,
        bookingWithTripIssuesLTM: true,
        bookingWithTripIssuesLTMPercentage: true,
        tripIssueTypeBreakdown: true,
        dslb: true,
        latestBooking: true,
        dslbLatestSnapshotDate: true,
        ltmGuestNps: true,
        vrboReviews: true,
        vrboReviewsScore: true,
        beyondPriceWeekday: true,
        beyondPriceWeekend: true,
        ltmNpsReviews: true,
        bookingCreationDates: true,
        asRampingPeriodEnd: true,
        asRampingPeriodStart: true,
        airbnbSearchImpressions: true,
        airbnbPDPConversionRate: true,
        airbnbBookingConversionRate: true,
        vrboSearchImpressions: true,
        vrboPDPConversionRate: true,
        vrboBookingConversionRate: true,
        directChannelsSearchImpressions: true,
        directChannelsPDPConversionRate: true,
        directChannelsBookingConversionRate: true,
        asYearlyUW: true,
        compSetAprWeekendMin: true,
        compSetAprWeekendMax: true,
        compSetAprWeekdayMin: true,
        compSetAprWeekdayMax: true,
        lyAdrWeekday: true,
        lyAdrWeekend: true,
        lyWeekdayBookedAtDates: true,
        lyWeekendBookedAtDates: true,
        badCompset: true,
        medianExpectedWeekdayPrice: true,
        medianExpectedWeekendPrice: true,
        bookingStatuses: true,
        propertyBlockNote: true,
        propertyBlockReason: true,
        asYearlyUWBear: true,
        asYearlyUWBull: true,
        avgStayPriceWeekday: true,
        avgStayPriceWeekend: true,
        medianExpectedWeekdayPriceV3: true,
        medianExpectedWeekendPriceV3: true,
      } satisfies PricingToolItemSelection,
    })
    .then(response => response.results ?? [])
}

export const getPricingToolMonthlyOccAndNetRevenue = async ({
  propertyId,
  startDate,
  endDate,
}: PricingToolMonthlyOccAndNetRevenueArgs) => {
  return arriereBackoffice.queries
    .pricingToolMonthlyOccAndNetRevenue({
      __alias: 'pricingToolMonthlyOccAndNetRevenue',
      __args: { propertyId, startDate, endDate },
      results: {
        month: true,
        avantStayWeekdayOccupancy: true,
        avantStayWeekendOccupancy: true,
        avantStayWeekdayNetRevenue: true,
        avantStayWeekendNetRevenue: true,
        compSetWeekdayOccupancy: true,
        compSetWeekendOccupancy: true,
      },
    })
    .then(response => response.results)
}

export const getRatingsOverTime = async ({
  propertyId,
  startDate,
  endDate,
}: GetRatingsOverTimeArgs) => {
  return arriereBackoffice.queries
    .getRatingsOverTime({
      __alias: 'getRatingsOverTime',
      __args: {
        propertyId,
        startDate,
        endDate,
      },
      results: {
        reviewMonth: true,
        cumulativeAirbnbRating: true,
        cumulativeNpsScore: true,
        cumulativeVrboRating: true,
        monthlyAirbnbRating: true,
        monthlyNpsScore: true,
        monthlyVrboRating: true,
      } satisfies RatingsOverTimeItemSelection,
    })
    .then(response => response.results)
}

export const getPricingToolReviewsOverTime = async ({
  propertyId,
  marketId,
  period,
}: {
  propertyId: string
  marketId: string
  period: { start: string; end: string }
}) => {
  return arriereBackoffice.queries.region
    .raw({
      __alias: 'getPricingToolReviewsOverTime',
      __args: { regionId: marketId },
      reviewsByBookingSources: {
        __args: {
          pagination: { page: 1, size: 1000 },
          periodKind: ReviewPeriodKind.reviewCreationDate,
          period,
          sources: [LeadSource.airBnb, LeadSource.vrbo],
          homeIds: [propertyId],
        },
        results: {
          bookingHash: true,
          source: true,
          guestName: true,
          period: {
            start: true,
            end: true,
          },
          reviews: {
            id: true,
            categoryReviews: {
              category: true,
              rating: true,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }
      return data.reviewsByBookingSources.results
    })
}

export const getPricingToolRevenueData = async ({
  propertyId,
  startDate,
  endDate,
}: PricingToolRevenueDataArgs) => {
  return arriereBackoffice.queries
    .pricingToolRevenueData({
      __alias: 'pricingToolRevenueData',
      __args: { propertyId, startDate, endDate },
      result: {
        periodAdr: true,
        ...pricingToolRevenueDataAdditionalSelection,
        periodOccupancy: true,
        periodRevPAU: true,
        rentalizer: true,
        revenue: true,
        compsetAdr: true,
        compsetOcc: true,
        compsetRevPAU: true,
        compsetMaxScrapedDate: true,
        dataIntegrity: { compset: { integrity: true, message: true } },
      },
    })
    .then(response => {
      if (!response.result) return response.result

      return {
        ...response.result,
        lighthouseOwnerAdr: getNullableNumberField(response.result, 'lighthouseOwnerAdr'),
      }
    })
}

export const getMaxCompsetDateScrapped = async ({ propertyId }: { propertyId: string }) => {
  return arriereBackoffice.queries.getCompsetMaxScrapedDateByPropertyId({
    __alias: 'getCompsetMaxScrapedDateByPropertyId',
    __args: { propertyId },
    result: {
      compsetMaxScrapedDate: true,
    },
  })
}

export const pricingToolRevenueCalendar = async ({
  propertyId,
  year,
  usePreAcquisitionData,
  includeRefunds,
}: PricingToolRevenueCalendarArgsWithRefunds) => {
  const queryArgs = {
    propertyId,
    year,
    usePreAcquisitionData,
    ...(includeRefunds === false ? { includeRefunds: false } : {}),
  } satisfies PricingToolRevenueCalendarArgsWithRefunds

  const resultsSelection = {
    year: true,
    yearlySummary: {
      yearlyAdr: true,
      yearlyArr: true,
      overallAdrRevenue: true,
      overallArrRevenue: true,
      overallOccupancy: true,
      overallRevPAU: true,
      overallRentPAN: true,
      yearlyRentCapacity: true,
      yearlyCapacity: true,
      yearlyRevenueTarget: true,
      lastYearOccupancy: true,
      sameTimeLastYearOccupancy: true,
      adrYearlyAvailableCapacity: true,
      arrYearlyAvailableCapacity: true,
      adrStlyRevenue: true,
      arrStlyRevenue: true,
      compsetAdr: true,
      yearlyBookings: true,
      yearlyBookedNights: true,
    },
    monthlySummary: {
      month: true,
      propertyAdr: true,
      arrWeekday: true,
      arrWeekend: true,
      adr: true,
      arr: true,
      revPar: true,
      revPAU: true,
      occupancy: true,
      adrRevenue: true,
      arrRevenue: true,
      sameTimeLastYearOccupancy: true,
      compsetAdr: true,
      adrWeekend: true,
      adrWeekday: true,
      rentPAN: true,
      adrCapacity: true,
      arrCapacity: true,
      underwrite: true,
      revenueTarget: true,
      revenueTargetSource: true,
      medianLeadTime: true,
      lastYearOccupancy: true,
      adrMonthlyAvailableCapacity: true,
      arrMonthlyAvailableCapacity: true,
      adrStlyRevenue: true,
      arrStlyRevenue: true,
      marketOccupancy: true,
      cohortExternalAdr: true,
      compsetRevPAU: true,
    },
    dailyData: {
      date: true,
      occupancyStatus: true,
      checkOutDate: true,
      propertyAdr: true,
      nightlyRate: true,
      publishedRate: true,
      propertyId: true,
      minStay: true,
      periodPropertyOccupancyBlockNote: true,
      fees: true,
      services: true,
      refundsAndCredits: true,
      otherAdjustments: true,
      appliedDiscounts: true,
      bookingsInfo: {
        bookingCode: true,
        bookingStatus: true,
        bookingSource: true,
        isBuyoutBooking: true,
        reservationType: true,
        bookedAt: true,
        checkIn: true,
        checkOut: true,
        bookingLength: true,
        leadTime: true,
        nightlyRate: true,
        appliedDiscount: true,
      },
    },
  } satisfies PricingToolRevenueCalendarItemSelectionWithAdditionalFields

  return arriereBackoffice.queries
    .pricingToolRevenueCalendar({
      __alias: 'pricingToolRevenueCalendar',
      __args: queryArgs,
      results: resultsSelection,
      preAcquisitionDataAvailable: true,
    })
    .then(response => {
      const years = response.results ?? []
      return {
        preAcquisitionDataAvailable: response.preAcquisitionDataAvailable ?? false,
        results: years.map(yearData => {
          const monthlySummary = yearData.monthlySummary?.map(month => ({
            ...month,
            revenueTarget: getNullableNumberField(month, 'revenueTarget'),
            revenueTargetSource: getNullableStringField(month, 'revenueTargetSource'),
          }))
          const yearlySummary = yearData.yearlySummary
            ? {
                ...yearData.yearlySummary,
                yearlyRevenueTarget: getNullableNumberField(
                  yearData.yearlySummary,
                  'yearlyRevenueTarget',
                ),
                overallRentPAN: getNullableNumberField(yearData.yearlySummary, 'overallRentPAN'),
              }
            : yearData.yearlySummary
          return {
            ...yearData,
            monthlySummary,
            yearlySummary,
            dailyData: yearData.dailyData?.map(day => {
              // sort bookings by bookingStatus - placing cancelled bookings last
              const bookingsInfo = day.bookingsInfo?.toSorted((a, b) =>
                (a.bookingStatus ?? '') > (b.bookingStatus ?? '') ? -1 : 1,
              )
              // extract `lead time` from the first non-cancelled booking
              const leadTime = day.bookingsInfo?.find(
                b => b.bookingStatus !== BookingStatus.cancelled,
              )?.leadTime
              return {
                ...day,
                bookingsInfo,
                leadTime,
                fees: getNullableNumberField(day, 'fees'),
                services: getNullableNumberField(day, 'services'),
                refundsAndCredits: getNullableNumberField(day, 'refundsAndCredits'),
                otherAdjustments: getNullableNumberField(day, 'otherAdjustments'),
                appliedDiscounts: getNullableNumberField(day, 'appliedDiscounts'),
              }
            }),
          }
        }),
      }
    })
}

export const getPricingToolBookingWindow = async (marketId: string) => {
  return arriereBackoffice.queries
    .bookingWindow({
      __alias: 'pricingToolBookingWindow',
      __args: { marketId },
      results: {
        completedRevenuePercentage: true,
        date: true,
        historicalAvgPercentage: true,
        market: true,
        marketId: true,
        stayMonth: true,
        weekendDate: true,
        cumulativeBookedRevenuePercentage: true,
        historicalAnnualAvgPercentage: true,
        inBookingWindow: true,
        referenceMonth: true,
      } satisfies BookingWindowItemSelection,
    })
    .then(response => response.results)
}

export const getPricingToolRevenueCapacityNext90Days = async (propertyId: string) => {
  return arriereBackoffice.queries
    .pricingToolRevenueCapacityNext90Days({
      __alias: 'pricingToolRevenueCapacityNext90Days',
      __args: { propertyId },
      result: {
        totalRevenueCapacity: true,
        totalBookedRevenue: true,
        totalAvailableRevenue: true,
        totalBlockedRevenue: true,
        totalAvailableNights: true,
        totalBlockedNights: true,
        totalBookedNights: true,
        weekdayTotalBookedRevenue: true,
        weekdayTotalBookedNights: true,
        weekdayTotalAvailableRevenue: true,
        weekdayTotalAvailableNights: true,
        weekdayTotalBlockedRevenue: true,
        weekdayTotalBlockedNights: true,
        weekdayTotalRevenueCapacity: true,
        weekendTotalBookedRevenue: true,
        weekendTotalBookedNights: true,
        weekendTotalAvailableRevenue: true,
        weekendTotalAvailableNights: true,
        weekendTotalBlockedRevenue: true,
        weekendTotalBlockedNights: true,
        weekendTotalRevenueCapacity: true,
      },
    })
    .then(response => {
      const data = response.result
      return {
        weekday: {
          revenueCapacity: data?.weekdayTotalRevenueCapacity,
          bookedRevenue: data?.weekdayTotalBookedRevenue,
          bookedNights: data?.weekdayTotalBookedNights,
          availableRevenue: data?.weekdayTotalAvailableRevenue,
          availableNights: data?.weekdayTotalAvailableNights,
          blockedRevenue: data?.weekdayTotalBlockedRevenue,
          blockedNights: data?.weekdayTotalBlockedNights,
        },
        weekend: {
          revenueCapacity: data?.weekendTotalRevenueCapacity,
          bookedRevenue: data?.weekendTotalBookedRevenue,
          bookedNights: data?.weekendTotalBookedNights,
          availableRevenue: data?.weekendTotalAvailableRevenue,
          availableNights: data?.weekendTotalAvailableNights,
          blockedRevenue: data?.weekendTotalBlockedRevenue,
          blockedNights: data?.weekendTotalBlockedNights,
        },
        all: {
          revenueCapacity: data?.totalRevenueCapacity,
          bookedRevenue: data?.totalBookedRevenue,
          bookedNights: data?.totalBookedNights,
          availableRevenue: data?.totalAvailableRevenue,
          availableNights: data?.totalAvailableNights,
          blockedRevenue: data?.totalBlockedRevenue,
          blockedNights: data?.totalBlockedNights,
        },
      }
    })
}

export const getPricingToolDemandMarketAverageL90d = async (marketId: string) => {
  return arriereBackoffice.queries
    .pricingToolL90DDemandFunnelMarketAverage({
      __alias: 'pricingToolL90DDemandFunnelMarketAverage',
      __args: { marketId },
      result: {
        airbnbAvgBookingConversion: true,
        airbnbAvgPdpConversion: true,
        airbnbAvgSearchResultImpressions: true,
        vrboAvgBookingConversion: true,
        vrboAvgPdpConversion: true,
        vrboAvgSearchResultImpressions: true,
        directAvgSearchResultImpressions: true,
        directAvgBookingConversion: true,
        directAvgPdpConversion: true,
      },
    })
    .then(response => response.result)
}

export const getPricingToolDemandMarketAverage = async (
  marketId: string,
  period: DemandFunnelMarketAveragePeriod,
) =>
  arriereBackoffice.queries.pricingToolDemandFunnelMarketAverage({
    __args: { marketId, period },
    airbnbAvgBookingConversion: true,
    airbnbAvgPdpConversion: true,
    airbnbAvgSearchResultImpressions: true,
    vrboAvgBookingConversion: true,
    vrboAvgPdpConversion: true,
    vrboAvgSearchResultImpressions: true,
    directAvgSearchResultImpressions: true,
    directAvgBookingConversion: true,
    directAvgPdpConversion: true,
  })

export const getPricingToolDataV2 = async ({ marketId, propertyId }: PricingToolArgs) => {
  return arriereBackoffice.queries
    .pricingToolV2({
      __alias: 'pricingToolDailyData',
      __args: {
        marketId,
        propertyId,
      },
      results: {
        refDate: true,
        propertyName: true,
        adnaCalendarDate: true,
        adnaPrice: true,
        ...pricingToolV2VrboPriceSelection,
        asAbnbReviewCount: true,
        asAbnbScore: true,
        asAdr: true,
        asAirbnbUrl: true,
        asAvailableNightsNext90Days: true,
        asBathrooms: true,
        asBedrooms: true,
        asDesirabilityScore: true,
        asHasAc: true,
        asHasCommunalPool: true,
        asHasHotTub: true,
        asHasPool: true,
        asLatitude: true,
        asLongitude: true,
        asMaxOccupancy: true,
        asMinStay: true,
        asPropertyIsWaterFront: true,
        asRampingPeriodEndDate: true,
        asRampingPeriodStartDate: true,
        asVrboUrl: true,
        propertyId: true,
        marketId: true,
        avgFloorRate: true,
        avgNetRevenue: true,
        bookingCodes: true,
        bookingCreationDates: true,
        compSetSize: true,
        season: true,
        leadTimes: true,
        market: true,
        medianExpectedPrice: true,
        ltmRevenue: true,
        bookingWithTripIssuesLTM: true,
        bookingWithTripIssuesLTMPercentage: true,
        tripIssueTypeBreakdown: true,
        dslb: true,
        ltmGuestNps: true,
        vrboReviews: true,
        vrboReviewsScore: true,
        vrboSearchImpressions: true,
        vrboBookingConversionRate: true,
        vrboPDPConversionRate: true,
        airbnbSearchImpressions: true,
        airbnbBookingConversionRate: true,
        airbnbPDPConversionRate: true,
        directChannelsSearchImpressions: true,
        directChannelsBookingConversionRate: true,
        directChannelsPDPConversionRate: true,
        compSetApr: true,
        avgYstd: true,
        compSetAprMax: true,
        compSetAprMin: true,
        beyondPrice: true,
        lyAdr: true,
        lyBookingCode: true,
        lyBookingCreationDate: true,
        extAdr: true,
        bookingStatuses: true,
        propertyBlockReason: true,
        propertyBlockNote: true,
        avgStayPrice: true,
        medianExpectedPriceV3: true,
        ...pricingToolV2AdditionalSelection,
      },
    })
    .then(response => response.results ?? [])
}

export const getRevenueTargets = ({ propertyId }: { propertyId: string }) => {
  return arriereBackoffice.queries
    .getPropertyRevenueTargets({
      __alias: 'getPropertyRevenueTargets',
      __args: { propertyId },
      results: {
        currentRevenueTarget: {
          quarter: true,
          quarterLabel: true,
          year: true,
          quarterRevenue: true,
          quarterRevenueTargetSource: true,
          quarterRevenueTargetAmount: true,
          quarterRevenueTargetDelta: true,
          quarterRevenueTargetPerformancePercentage: true,
          quarterRevenueTargetVariancePercentage: true,
        },
      },
    })
    .then(response => response.results)
}

export const getCompsetDataV2 = ({
  propertyId,
  filters,
  compsetSize = 50,
}: PricingToolCompsetV2Args) => {
  return arriereBackoffice.queries
    .pricingToolCompsetV2({
      __alias: 'getCompsetData',
      __args: {
        filters,
        propertyId,
        compsetSize,
      },
      results: {
        asPropertyId: true,
        asPropertyName: true,
        airbnbUrl: true,
        vrboUrl: true,
        similarityScore: true,
        desirabilityScore: true,
        distance: true,
        overallRating: true,
        numberOfReviews: true,
        minStay: true,
        hasPool: true,
        hasAC: true,
        hasHotTub: true,
        hasCommunalPool: true,
        isWaterFront: true,
        maxOccupancy: true,
        bathrooms: true,
        bedrooms: true,
        isAvantstay: true,
        apr: true,
        adr: true,
        photoUrls: true,
        latitude: true,
        longitude: true,
        aprWeekday: true,
        aprWeekend: true,
        adrWeekday: true,
        adrWeekend: true,
        ltmRevenue: true,
        cleaningFee: true,
        propertyName: true,
        externalPropertyId: true,
        allowPets: true,
        tier: true,
      },
    })
    .then(response => response.results)
}

export const getPropertyRevenuePacing = async ({
  propertyId,
  startDate,
  endDate,
}: {
  propertyId: string
  startDate: IDate
  endDate: IDate
}) => {
  return arriereBackoffice.queries
    .propertyRevenuePacing({
      __alias: 'getPropertyRevenuePacing',
      __args: {
        propertyId,
        startDate,
        endDate,
      },
      results: {
        propertyId: true,
        month: true,
        revenue: true,
        revenueTargetAmount: true,
        revenueTargetSource: true,
        revenueSameTimeLastYear: true,
        revenueTargetDelta: true,
        revenueTargetPerformancePercentage: true,
        cumulativeRevenueTargetAmount: true,
        revenueTargetVariancePercentage: true,
        emptyRevenueTargetReason: true,
      },
    })
    .then(response => response.results)
}

export const getFloorRateImpactCalculatorResults = async ({
  propertyId,
  floorRate,
}: {
  propertyId: string
  floorRate: number
}) => {
  return arriereBackoffice.queries
    .getFloorRateImpactCalculatorResults({
      __alias: 'getFloorRateImpactCalculatorResults',
      __args: {
        propertyId,
        floorRate,
      },
      result: {
        cohortImpact: {
          cohort: true,
          revenueImpact: true,
          revenueImpactPercentage: true,
          totalRevenueForecast: true,
          totalRevenueForecastWithFloorRate: true,
          projections: {
            adr: true,
            date: true,
            occupancy: true,
            revPAR: true,
            revPARWithFloorRate: true,
          },
        },
      },
    })
    .then(response => response.result?.cohortImpact)
}

export const getRecentBookings = ({ propertyId }: RecentBookingsArgs) => {
  return arriereBackoffice.queries
    .recentBookings({
      __alias: 'getRecentBookings',
      __args: {
        propertyId,
      },
      results: {
        checkInDate: true,
        checkOutDate: true,
        bookingDate: true,
        bookingCancellationDate: true,
        lengthOfStay: true,
        leadTime: true,
        bookingSource: true,
        cancelledBooking: true,
        bookingHash: true,
        bookingId: true,
        guestCount: true,
        totalNightsRevenue: true,
        totalRevenue: true,
        lyLeadTime: true,
        nightly: true,
        lyNightly: true,
        adr: true,
        lyAdr: true,
        isBuyoutBooking: true,
        appliedDiscount: true,
        reviewed: true,
        reviewedByName: true,
        reviewedByEmail: true,
        reviewedAt: true,
        reviewComment: true,
        flags: {
          key: true,
          glyph: true,
          label: true,
          tooltip: true,
        },
      },
    })
    .then(response => response.results)
}

export const getPricingToolChannelBreakdown = ({
  propertyId,
  startDate,
  endDate,
}: PricingToolRevenueChannelBreakdownArgs) => {
  return arriereBackoffice.queries
    .pricingToolRevenueChannelBreakdown({
      __alias: 'getPricingToolChannelBreakdown',
      __args: { propertyId, startDate, endDate },
      result: {
        channel: true,
        absoluteRevenue: true,
        percentageRevenue: true,
        absoluteBookings: true,
        percentageBookings: true,
      },
    })
    .then(response => response.result)
}

export const getPricingToolPropertySignalsPriority = async (marketId: string) => {
  return arriereBackoffice.queries
    .propertySignalsPriority({
      __alias: 'getPricingToolPropertySignalsPriority',
      __args: { filter: { marketIds: [marketId] } },
      results: {
        marketId: true,
        signalCount: true,
        priorityPosition: true,
        previousPriorityPosition: true,
        propertyName: true,
        propertyId: true,
        rmAnalyst: true,
        rmMarket: true,
        signals: {
          id: true,
          resolvedAt: true,
          detectedAt: true,
          displayName: true,
          propertyId: true,
          metadata: true,
          signalType: {
            id: true,
            category: true,
            code: true,
            name: true,
            description: true,
          },
        },
      },
    })
    .then(response => response.results)
}

export const getPricingToolPropertiesOptions = async ({
  marketId,
}: PricingToolPropertyOptionsArgs) => {
  const [response, hotelNamesById] = await Promise.all([
    arriereBackoffice.queries.pricingToolPropertyOptions({
      __alias: 'pricingToolPropertyOptions',
      __args: {
        marketId,
      },
      results: {
        propertyId: true,
        propertyName: true,
        thumbnailUrl: true,
        marketId: true,
        marketName: true,
        analystReviewStatus: true,
        analystSentiment: true,
        snoozeDate: true,
        dslb: true,
        revenueLtm: true,
        currentMonthRevenue: true,
        currentMonthRevenueTargetAmount: true,
        currentMonthTargetPacing: true,
        currentQuarterRevenue: true,
        currentQuarterRevenueTargetAmount: true,
        currentQuarterTargetPacing: true,
        ntmRevenueTargetAmount: true,
        n30dOccupancy: true,
        n90dOccupancy: true,
        latestReviewInfo: {
          userEmail: true,
          userName: true,
          latestInteractionCreatedAt: true,
        },
        rmAnalyst: true,
        rmMarket: true,
        rampingPeriod: {
          startDate: true,
          endDate: true,
          currentlyRamping: true,
        },
        home: {
          revenueTags: true,
          isAncestor: true,
          hotelId: true,
        },
        worklistStatus: true,
        worklistStatusLabel: true,
        worklistReviewCycle: true,
        worklistReviewOrder: true,
        worklistDone: true,
      } satisfies PricingToolPropertyOptionsItemSelection,
    }),
    getHotelNamesById(),
  ])

  return (response.results ?? []).map(property => {
    const revenueTags = property.home?.revenueTags ?? []
    return {
      ...property,
      revenueTags,
      isVipHome: revenueTags.includes('VIP Home'),
      isBuyout: property.home?.isAncestor ?? false,
      buildingName: property.home?.hotelId ? (hotelNamesById[property.home.hotelId] ?? null) : null,
    }
  })
}

interface GetPricingToolWorklistParams {
  analystUserId?: string
  statuses: PricingToolWorklistStatus[]
  reviewedIn?: PricingToolWorklistReviewedIn
}

const WORKLIST_LIMIT = 5000

export const getPricingToolWorklist = async ({
  analystUserId,
  statuses,
  reviewedIn,
}: GetPricingToolWorklistParams) => {
  const worklist = await arriereBackoffice.queries.pricingToolWorklist({
    __alias: reviewedIn ? 'pricingToolReviewedWorklist' : 'pricingToolWorklist',
    __args: {
      input: reviewedIn
        ? { analystUserId, reviewedIn, limit: WORKLIST_LIMIT }
        : { analystUserId, statuses, limit: WORKLIST_LIMIT },
    },
    run: {
      planDate: true,
      planWeek: true,
      publishedAt: true,
      stale: true,
    },
    total: true,
    items: {
      propertyId: true,
      propertyName: true,
      home: {
        thumbnail: {
          url: true,
        },
      },
      marketName: true,
      bedrooms: true,
      status: true,
      priorityStatus: true,
      statusReason: true,
      isVip: true,
      vipBasis: true,
      isUnderperforming: true,
      underperformingBenchmark: true,
      underperformingGapPct: true,
      flags: {
        bookingWindow: true,
        reservations: true,
        ssPacing: true,
        dslb: true,
        bookings7d: true,
        count: true,
      },
      flaggedReservations: true,
      newAssetBucket: true,
      newAssetDue: true,
      isLtr: true,
      isOffboarding: true,
      ssDelta: true,
      dslb: true,
      bookings7d: true,
      daysSinceReview: true,
      snoozeDate: true,
      dueWeek: true,
      dueWeekSource: true,
      isDue: true,
      latestNote: true,
      lastReviewedAt: true,
      lastReviewedBy: true,
      workToday: true,
      scheduleState: true,
      scheduleGroup: true,
      scheduledWeekdays: true,
    },
  })

  return {
    ...worklist,
    run: worklist.run
      ? { ...worklist.run, planWeek: formatDate(worklist.run.planWeek, SHORT_ISO_DATE_FORMAT) }
      : worklist.run,
    items: worklist.items.map(item => ({
      ...item,
      dueWeek: formatDate(item.dueWeek, SHORT_ISO_DATE_FORMAT),
    })),
  }
}

interface GetPricingToolTeamWorklistParams {
  statuses: PricingToolWorklistStatus[]
  limit: number
}

export const getPricingToolTeamWorklist = async ({
  statuses,
  limit,
}: GetPricingToolTeamWorklistParams) => {
  return arriereBackoffice.queries.pricingToolWorklist({
    __alias: 'pricingToolTeamWorklist',
    __args: {
      input: { allAnalysts: true, statuses, limit },
    },
    run: {
      planDate: true,
      stale: true,
    },
    items: {
      propertyId: true,
      analystUserId: true,
      analystName: true,
      status: true,
      isVip: true,
      isUnderperforming: true,
      flags: {
        count: true,
      },
      daysSinceReview: true,
      workToday: true,
    },
  })
}

export const getPricingToolPropertiesAnalystSentimentSummary = async () => {
  return arriereBackoffice.queries
    .pricingToolPropertiesAnalystSentimentSummary({
      __alias: 'pricingToolPropertiesAnalystSentimentSummary',
      __args: {},
      results: {
        marketId: true,
        marketName: true,
        firstPropertyId: true,
        totalProperties: true,
        propertiesByAnalystSentiment: {
          NOT_REVIEWED: true,
          BAD: true,
          UNDERPERFORMING: true,
          AT_RISK: true,
          MEETS_EXPECTATIONS: true,
          STRONG: true,
          OUTSTANDING: true,
        },
      },
    })
    .then(response => response.results ?? [])
}

export const getPricingToolMarketSignalsSummary = async () => {
  return arriereBackoffice.queries
    .pricingToolMarketSignalsSummary({
      __alias: 'getPricingToolMarketSignalsSummary',
      results: {
        marketId: true,
        marketName: true,
        propertyCount: true,
        analysts: true,
        analystSentiments: {
          label: true,
          count: true,
        },
        reviewCycleIntervals: {
          label: true,
          count: true,
        },
        signals: {
          label: true,
          count: true,
        },
      },
    })
    .then(response => response.results ?? [])
}

export const getPricingToolMarketPropertyDetails = async (marketId: string) => {
  return arriereBackoffice.queries
    .pricingToolMarketPropertyDetails({
      __alias: 'getPricingToolMarketPropertyDetails',
      __args: { marketId },
      results: {
        propertyId: true,
        propertyName: true,
        analystSentiment: true,
        snoozeDate: true,
        signals: {
          label: true,
          detectedAt: true,
          metadata: {
            actualValue: true,
            matchedCondition: {
              description: true,
            },
          },
        },
        home: {
          thumbnail: { url: true },
          revenueTags: true,
        },
      },
    })
    .then(response => response.results ?? [])
}

export const getPricingToolRevPau = async (propertyId: string, months = 12) => {
  const startDate = subMonths(new Date(), months)
  const endDate = new Date()
  return arriereBackoffice.queries
    .pricingToolRevPau({
      __alias: 'getPricingToolRevPau',
      __args: {
        propertyId,
        startDate: formatDate(startDate, SHORT_ISO_DATE_FORMAT),
        endDate: formatDate(endDate, SHORT_ISO_DATE_FORMAT),
      },
      result: {
        periodOccupancy: true,
        periodAdr: true,
        periodRevPAU: true,
      },
    })
    .then(response => response.result)
}

export const getNegativeReviews = ({ propertyId, maxRating }: PricingToolNegativeReviewsArgs) => {
  return arriereBackoffice.queries
    .pricingToolNegativeReviews({
      __alias: 'getNegativeReviews',
      __args: {
        propertyId,
        maxRating,
      },
      results: {
        propertyId: true,
        bookingHash: true,
        bookingLeadSource: true,
        bookingId: true,
        overallReviewDate: true,
        overallRating: true,
        accuracyRating: true,
        checkInRating: true,
        cleanlinessRating: true,
        communicationRating: true,
        locationRating: true,
        valueRating: true,
        npsRating: true,
        guests: true,
        checkInDate: true,
        checkOutDate: true,
        isVip: true,
        internalReservationType: true,
        privateFeedback: true,
        overallComment: true,
      },
    })
    .then(response => response.results)
}

const HIGHEST_REVIEW_RATING = 5
const PROPERTY_REVIEWS_MAX_AGE = 30 * 60 * 1000
const PROPERTY_REVIEWS_MAX_SIZE = 4

const resolvedPropertyReviews = new Map<string, Awaited<ReturnType<typeof getNegativeReviews>>>()

export const getPropertyReviews = memoize(
  async (propertyId: string) => {
    const reviews = await getNegativeReviews({ propertyId, maxRating: HIGHEST_REVIEW_RATING })
    resolvedPropertyReviews.set(propertyId, reviews)
    return reviews
  },
  {
    isPromise: true,
    maxAge: PROPERTY_REVIEWS_MAX_AGE,
    maxSize: PROPERTY_REVIEWS_MAX_SIZE,
    onExpire: ([propertyId]: Key<string>) => resolvedPropertyReviews.delete(propertyId),
  },
)

// Same reason as `peekPropertyEditHistory`: skip the one-tick PENDING flash on a settled promise.
export const peekPropertyReviews = (propertyId: string) => resolvedPropertyReviews.get(propertyId)

export const getBasePriceChanges = (propertyId: string) => {
  return arriereBackoffice.queries
    .basePriceChanges({
      __alias: 'getBasePriceChanges',
      __args: {
        propertyId,
      },
      results: {
        changeDate: true,
        previousBaseRate: true,
        baseRate: true,
        propertyId: true,
      },
    })
    .then(response => response.results)
}

export const getMetadataBasePriceChanges = (propertyId: string) => {
  return arriereBackoffice.queries
    .pricingToolBasePriceChangesMetadata({
      __alias: 'pricingToolBasePriceChangesMetadata',
      __args: {
        propertyId,
      },
      results: {
        userEmail: true,
        percentChange: true,
        previousBasePrice: true,
        newBasePrice: true,
        comment: true,
        tagsSelected: true,
        analystSentiment: true,
        snoozeDate: true,
        id: true,
        createdAt: true,
        propertyId: true,
        userName: true,
      },
    })
    .then(response => response.results)
}

export const getLastYearAdr = (propertyId: string) => {
  return arriereBackoffice.queries
    .pricingToolLastYearAdr({
      __alias: 'getPricingToolLastYearAdr',
      __args: {
        propertyId,
      },
      results: {
        weekendDate: true,
        weekdayDate: true,
        propertyId: true,
        asAdrWeekend: true,
        asAdrWeekday: true,
        asAdr: true,
        bookingHash: true,
      },
    })
    .then(response => response.results)
}

export const getPropertyCleaningFees = (propertyId: string) => {
  return arriereBackoffice.queries
    .pricingToolPropertyCleaningFee({
      __alias: 'getPropertyCleaningFees',
      __args: {
        propertyId,
      },
      results: {
        propertyId: true,
        directCleaningFeeRate: true,
        directMarginalCleaningFeeRate: true,
        airbnbCleaningFeeRate: true,
        airbnbMarginalCleaningFeeRate: true,
        vrboCleaningFeeRate: true,
        vrboMarginalCleaningFeeRate: true,
        cleaningVendorRate: true,
        laundryVendorRate: true,
        totalCleaningCost: true,
        directTotalCleaningFeeRate: true,
        cleaningMarginRate: true,
        cleaningMarginPercentage: true,
        cleaningCostBreakdown: {
          total: true,
          items: {
            item: true,
            cost: true,
          },
        },
      },
    })
    .then(response => response.results)
}

export const getPropertyBasePrice = async ({ propertyId }: BasePriceArgs) => {
  return arriereBackoffice.queries
    .basePrice({
      __alias: 'getPropertyBasePrice',
      __args: {
        propertyId,
      },
      results: {
        propertyId: true,
        baseRate: true,
      },
    })
    .then(response => response.results?.find(i => i.propertyId === propertyId)?.baseRate)
}

export const PRICING_TOOL_EVENT_TIMELINE_PAGE_SIZE = 100

export const getPricingToolEventTimeline = ({
  propertyId,
  eventType,
  pagination = { page: 1, size: PRICING_TOOL_EVENT_TIMELINE_PAGE_SIZE },
}: PricingToolEventTimelineArgs) => {
  return arriereBackoffice.queries
    .pricingToolEventTimeline({
      __alias: 'getPricingToolEventTimeline',
      __args: {
        propertyId,
        eventType,
        pagination,
      },
      results: {
        id: true,
        eventType: true,
        userId: true,
        userEmail: true,
        createdAt: true,
        previousBasePrice: true,
        newBasePrice: true,
        comment: true,
        analystSentiment: true,
        snoozeDate: true,
        tagsSelected: true,
        userName: true,
        updatedAt: true,
        canBeEdited: true,
        versions: {
          version: true,
          comment: true,
          tagsSelected: true,
          analystSentiment: true,
          snoozeDate: true,
          editedBy: true,
          editedAt: true,
        },
      },
    })
    .then(response => {
      const events = response.results ?? []
      return events.sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
      )
    })
}

export const savePricingToolReviewNoChanges = ({
  propertyId,
  marketId,
  userId,
  userEmail,
  userName,
}: Pick<
  SavePricingToolReviewNoChangesArgs,
  'propertyId' | 'marketId' | 'userId' | 'userEmail' | 'userName'
>) => {
  return arriereBackoffice.mutations.savePricingToolReviewNoChanges({
    __args: {
      propertyId,
      marketId,
      userId,
      userEmail,
      userName,
    },
    success: true,
    message: true,
  })
}

export const reviewRecentBooking = ({
  bookingId,
  propertyId,
  reviewed,
  comment,
  userId,
  userEmail,
  userName,
}: ReviewRecentBookingArgs) => {
  return arriereBackoffice.mutations.reviewRecentBooking({
    __args: {
      bookingId,
      propertyId,
      reviewed,
      comment,
      userId,
      userEmail,
      userName,
    },
    success: true,
    message: true,
  })
}

export const editNote = ({
  id,
  comment,
  tagsSelected,
  analystSentiment,
}: Pick<EditNoteArgs, 'id' | 'comment' | 'tagsSelected' | 'analystSentiment'>) => {
  return arriereBackoffice.mutations.editNote({
    __args: {
      id,
      comment,
      tagsSelected,
      analystSentiment,
    },
    success: true,
    message: true,
  })
}

export const getPricingToolSeasons = async ({ marketId, startDate, endDate }: SeasonsArgs) => {
  return arriereBackoffice.queries
    .seasons({
      __alias: 'getPricingToolSeasons',
      __args: {
        marketId,
        startDate,
        endDate,
      },
      results: {
        seasonName: true,
        marketId: true,
        seasonType: true,
        startDate: true,
        endDate: true,
      },
    })
    .then(response => response.results)
}

export const getPricingToolBlocks = async ({
  propertyId,
  startDate,
  endDate,
}: PropertyBlocksArgs) => {
  return arriereBackoffice.queries
    .propertyBlocks({
      __alias: 'getPricingToolBlocks',
      __args: {
        propertyId,
        startDate,
        endDate,
      },
      results: {
        propertyId: true,
        startDate: true,
        endDate: true,
        periodPropertyOccupancyStatus: true,
        periodPropertyOccupancyBlockReason: true,
        periodPropertyOccupancyBlockSubReason: true,
        periodPropertyOccupancyBlockNote: true,
      },
    })
    .then(response => response.results)
}

export const getPricingToolMinimumStayCalendar = async ({
  propertyId,
  startDate,
  endDate,
}: PricingToolMinimumStayCalendarArgs) => {
  return arriereBackoffice.queries.pricingToolMinimumStayCalendar
    .raw({
      __alias: 'getPricingToolMinimumStayCalendar',
      __args: {
        propertyId,
        startDate,
        endDate,
      },
      results: {
        date: true,
        minimumStay: true,
        avgClusterLengthOfStay: true,
      },
    })
    .then(({ data, errors }) => {
      if (errors) {
        throw Error(errors[0]?.message)
      }
      return data.results
    })
}

export const getDirectStays = ({ propertyId }: DirectStaysArgs) => {
  return arriereBackoffice.queries
    .directStays({
      __alias: 'getDirectStays',
      __args: {
        propertyId,
      },
      results: {
        propertyId: true,
        checkInDate: true,
        checkOutDate: true,
        bookingCodes: true,
        referenceDate: true,
      },
    })
    .then(response => response.results)
}

export const getPropertyCurrentListingRating = (propertyId: string) => {
  return arriereBackoffice.queries
    .home({
      __args: { homeId: propertyId },
      __alias: 'currentListingRating',
      currentListingRatings: {
        sourceListingId: true,
        source: true,
        categories: {
          category: true,
          rating: true,
          numberOfReviews: true,
        },
      },
    })
    .then(response => response.currentListingRatings)
}

export type RevenuePriceMove = {
  settingFamily: string
  targetDateStart: string
  targetDateEnd: string
  priorValue: number | null
  newValue: number | null
  magnitudeUnit: string
  direction: string
  realizedPct: number | null
  isBaseline: boolean
  decisionId: string | null
  isBulk: boolean
  bulkSize: number
  bulkGroupId: string | null
}

export type RevenueConfigChange = {
  settingFamily: string
  summary: string
  previousBasePrice: number | null
  newBasePrice: number | null
  pctChange: number | null
  decisionId: string | null
  isBulk: boolean
  bulkSize: number
  bulkGroupId: string | null
}

export type RevenueTimelineNote = {
  date: string
  userName: string | null
  sentiment: string | null
  comment: string
}

export type RevenueActionsTimelineEntry = {
  date: string
  actorType: string
  actorName: string | null
  moves: RevenuePriceMove[]
  configChanges: RevenueConfigChange[]
}

export type RevenueActionsTimeline = {
  propertyId: string
  entries: RevenueActionsTimelineEntry[]
  notes: RevenueTimelineNote[]
}

export type RevenueActionsTimelineData = RevenueActionsTimeline

export const getRevenueActionsTimeline = (
  params: RevenueActionsTimelineArgs,
): Promise<RevenueActionsTimelineData> => {
  return arriereBackoffice.queries
    .revenueActionsTimeline({
      __alias: 'getRevenueActionsTimeline',
      __args: params,
      propertyId: true,
      entries: {
        date: true,
        actorType: true,
        actorName: true,
        moves: {
          settingFamily: true,
          targetDateStart: true,
          targetDateEnd: true,
          priorValue: true,
          newValue: true,
          magnitudeUnit: true,
          direction: true,
          realizedPct: true,
          isBaseline: true,
          decisionId: true,
          isBulk: true,
          bulkSize: true,
          bulkGroupId: true,
        },
        configChanges: {
          settingFamily: true,
          summary: true,
          previousBasePrice: true,
          newBasePrice: true,
          pctChange: true,
          decisionId: true,
          isBulk: true,
          bulkSize: true,
          bulkGroupId: true,
        },
      },
      notes: {
        date: true,
        userName: true,
        sentiment: true,
        comment: true,
      },
    })
    .then(
      (resp): RevenueActionsTimelineData =>
        (resp ?? {
          propertyId: params.propertyId,
          entries: [],
          notes: [],
        }) as RevenueActionsTimelineData,
    )
}

export type PropertyEditEntry = {
  time: string
  kind: HomeHistorySnapshotKind
  authorName: string
  authorKind: string
  data?: PropertyEditEntryData
}

export type PropertyEditHistoryParams = {
  propertyId: string
  startDate?: string
  endDate?: string
}

const PROPERTY_EDIT_HISTORY_MAX_AGE = 30 * 60 * 1000
const PROPERTY_EDIT_HISTORY_MAX_SIZE = 4

const startOfDayISO = (date: string) => startOfDay(parseISO(date)).toISOString()
const endOfDayISO = (date: string) => endOfDay(parseISO(date)).toISOString()

export type PropertyEditHistory = {
  entries: PropertyEditEntry[]
  limitedSince?: string
}

const resolvedEditHistory = new Map<string, PropertyEditHistory>()

const editHistoryCacheKey = ({ propertyId, startDate, endDate }: PropertyEditHistoryParams) =>
  `${propertyId}:${startDate ?? ''}:${endDate ?? ''}`

const HOME_HISTORY_RANGE_ERROR = /exceed twelve months/i
const HOME_HISTORY_MAX_MONTHS = 12

const isHomeHistoryRangeError = (error: unknown) =>
  HOME_HISTORY_RANGE_ERROR.test(error instanceof Error ? error.message : String(error))

const HOME_SETTINGS_SELECTION = {
  name: true,
  title: true,
  maxOccupancy: true,
  maxAdults: true,
  bathroomsCount: true,
  halfBathroomsCount: true,
  area: true,
  floorsCount: true,
  shortTermAllowed: true,
  longTermAllowed: true,
  couponsAllowed: true,
  shortDescription: true,
  description: true,
  address: { address: true, city: true, zipCode: true },
} as const

const HOME_DETAILS_CHANGE_SELECTION = {
  ownerName: true,
  maintenanceStatus: true,
  revenueModel: true,
  assetType: true,
  pmRate: true,
  totRate: true,
  market: true,
  activeSince: true,
  activeUntil: true,
  publishHighRisk: true,
  wheelchairAccessible: true,
} as const

const LISTING_TEXT_SELECTION = {
  title: true,
  description: {
    text: true,
    summary: true,
    space: true,
    access: true,
    neighborhood: true,
    transit: true,
    interaction: true,
    houseRules: true,
    notes: true,
  },
} as const

const SEASONAL_POLICY_STATE_SELECTION = {
  checkInFrom: true,
  checkInTo: true,
  channelCancellationPolicyCategoryId: true,
  label: true,
  enabled: true,
  priority: true,
}

const homeHistoryQuery = (propertyId: string, startDate?: string, endDate?: string) =>
  arriereBackoffice.queries.homeHistory({
    __alias: 'getPropertyEditHistory',
    __args: {
      homeId: propertyId,
      ...(startDate && { start: startOfDayISO(startDate) }),
      ...(endDate && { end: endOfDayISO(endDate) }),
    },
    time: true,
    kind: true,
    author: {
      name: true,
      kind: true,
    },
    data: {
      home: { from: HOME_SETTINGS_SELECTION, to: HOME_SETTINGS_SELECTION },
      homeDetails: { from: HOME_DETAILS_CHANGE_SELECTION, to: HOME_DETAILS_CHANGE_SELECTION },
      homeImage: { description: true, listOrder: true },
      homeImageChange: {
        from: { description: true, listOrder: true },
        to: { description: true, listOrder: true },
      },
      coverImageChanges: { impactedAgencies: true },
      amenitiesChange: {
        from: { amenityId: true, quantity: true, comment: true },
        to: { amenityId: true, quantity: true, comment: true },
      },
      amenities: { amenityId: true },
      customFieldData: {
        customFieldName: true,
        valueTo: true,
        valueChange: { from: { valueJson: true }, to: { valueJson: true } },
      },
      room: {
        name: true,
        kind: true,
        description: true,
        listOrder: true,
        maxOccupancy: true,
        area: true,
        bathroomKind: true,
      },
      serviceUpdate: {
        serviceId: true,
        enabled: true,
        price: true,
        priority: true,
        approvalRule: true,
        newCleaningRatio: true,
        newNightlyRatio: true,
        earlyCheckInStartTime: true,
        lateCheckOutEndTime: true,
      },
      distributionChanges: { agency: true, channel: true, active: true, listingKind: true },
      buyoutChanges: { buyoutsAdded: true, buyoutsRemoved: true },
      homeLease: {
        ownerName: true,
        revenueModel: true,
        assetType: true,
        pmRate: true,
        totRate: true,
        amount: true,
        signDate: true,
        startDate: true,
        endDate: true,
        financialLaunchDate: true,
        notes: true,
      },
      homeListing: {
        agency: true,
        listing: { from: LISTING_TEXT_SELECTION, to: LISTING_TEXT_SELECTION },
        channelTitleChanges: { ota: true, title: { from: true, to: true } },
      },
      updateCancellationPolicy: { agency: true, channelCancellationPolicyCategoryId: true },
      seasonalCancellationPolicyChange: {
        from: SEASONAL_POLICY_STATE_SELECTION,
        to: SEASONAL_POLICY_STATE_SELECTION,
      },
      paymentPlanCategories: true,
    },
  })

type PropertyEditQueryEntry = NonNullable<Awaited<ReturnType<typeof homeHistoryQuery>>>[number]

export type PropertyEditEntryData = PropertyEditQueryEntry['data']

export const getPropertyEditHistory = memoize(
  async ({
    propertyId,
    startDate,
    endDate,
  }: PropertyEditHistoryParams): Promise<PropertyEditHistory> => {
    let limitedSince: string | undefined

    const entries = await homeHistoryQuery(propertyId, startDate, endDate)
      .catch((error: unknown) => {
        if (!isHomeHistoryRangeError(error)) throw error

        limitedSince = formatDate(
          addDays(subMonths(new Date(), HOME_HISTORY_MAX_MONTHS), 1),
          SHORT_ISO_DATE_FORMAT,
        )
        return homeHistoryQuery(propertyId, limitedSince, endDate)
      })
      .catch((error: unknown) => {
        trackError(
          `Attempt to fetch homeHistory data failed: ${error instanceof Error ? error.message : String(error)}`,
          {
            propertyId,
            startDate,
            endDate,
            lookbackDays: startDate
              ? differenceInCalendarDays(new Date(), parseISO(startDate))
              : null,
          },
        )
        throw error
      })

    const history = {
      entries: (entries ?? []).map(entry => ({
        time: String(entry.time),
        kind: entry.kind,
        authorName: entry.author?.name ?? 'Unknown',
        authorKind: entry.author?.kind ?? '',
        data: entry.data,
      })),
      limitedSince,
    }

    resolvedEditHistory.set(editHistoryCacheKey({ propertyId, startDate, endDate }), history)

    return history
  },
  {
    isPromise: true,
    isDeepEqual: true,
    maxAge: PROPERTY_EDIT_HISTORY_MAX_AGE,
    // One slot per period, so switching back is a cache hit instead of a refetch.
    maxSize: PROPERTY_EDIT_HISTORY_MAX_SIZE,
    onExpire: ([params]: Key<PropertyEditHistoryParams>) =>
      resolvedEditHistory.delete(editHistoryCacheKey(params)),
  },
)

// Same reason as `peekPropertyChanges`: `isPending` reports PENDING for a tick even
// on a settled promise, which would flash the skeleton on every period switch.
export const peekPropertyEditHistory = (params: PropertyEditHistoryParams) =>
  resolvedEditHistory.get(editHistoryCacheKey(params))

export const getAmenityNames = memoize(
  () =>
    arriereBackoffice.queries.amenities({
      __alias: 'getAmenityNames',
      id: true,
      name: true,
    }),
  { isPromise: true },
)

export const getCancellationPolicyNames = memoize(
  () =>
    arriereBackoffice.queries.cancellationPolicyCategories({
      __alias: 'getCancellationPolicyNames',
      id: true,
      name: true,
    }),
  { isPromise: true },
)

export const getServiceNames = memoize(
  () =>
    arriereBackoffice.queries.services({
      __alias: 'getServiceNames',
      id: true,
      name: true,
    }),
  { isPromise: true },
)

export const getPaymentPlanNames = memoize(
  () =>
    arriereBackoffice.queries.paymentPlanCategories({
      __alias: 'getPaymentPlanNames',
      id: true,
      name: true,
    }),
  { isPromise: true },
)
